import { airHandlerSizes } from 'data/constants';
import { WheelEvent } from 'react';
import { fontFamily as tailwindFonts } from 'tailwindcss/defaultTheme';

export function isEmpty(value: any) {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  );
}

export function isNotEmpty(value: any) {
  return !isEmpty(value);
}

export function parseCoordinate(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function abbreviateNumber(value: number): string {
  const thresholds = [
    { value: 1e12, suffix: 'T' },
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' }
  ];

  for (const threshold of thresholds) {
    if (value >= threshold.value) {
      const divided = value / threshold.value;
      const truncated = Math.floor(divided * 10) / 10;
      const formatted =
        truncated % 1 === 0 ? truncated.toFixed(0) : truncated.toFixed(1);
      return formatted + threshold.suffix;
    }
  }

  return value.toString();
}

export function roundToDecimal(value: number, decimalPlaces: number) {
  return Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

export function formatUSD(value: number, showZeroCents = false) {
  const hasCents = value % 1 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents || showZeroCents ? 2 : 0
  }).format(value);
}

export function formatPrice(price: number): string {
  if (!Number.isFinite(price)) return '$—';
  return `$${Math.round(price).toLocaleString()}`;
}

export function getNthPercentile(values: number[], percentile: number): number {
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((percentile / 100) * sorted.length) - 1)
  );
  return sorted[index] || 0;
}

export function removeNullishValues<T>(obj: T & object): T {
  if (typeof obj === 'object') {
    const newObj = structuredClone(obj);
    Object.keys(newObj).forEach((key) => {
      if (newObj[key] && typeof newObj[key] === 'object') {
        newObj[key] = removeNullishValues(newObj[key]);
      } else if (newObj[key] === null || newObj[key] === undefined) {
        // eslint-disable-next-line no-param-reassign
        delete newObj[key];
      }
    });
    return newObj;
  }
  return obj;
}

export function measureTextWidth(
  text: string,
  fontSize: number = 16,
  fontFamily: keyof typeof tailwindFonts = 'sans'
) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return 0;
  }
  context.font = `${fontSize}px ${tailwindFonts[fontFamily].join(', ')}`;
  const { width } = context.measureText(text);
  canvas.remove();
  return Math.round(width);
}

export function fileToBase64(file: File | Blob) {
  return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

export async function imageUrlToBase64(url: string) {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const MAX_IMAGE_WIDTH = 1920;
const MAX_IMAGE_HEIGHT = 1920;
const IMAGE_ENCODE_QUALITY = 0.92;

export function loadImageElement(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = objectUrl;
  });
}

export function canvasToFile(
  canvas: HTMLCanvasElement,
  name: string,
  type: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode image'));
          return;
        }
        resolve(
          new File([blob], name, { type: blob.type || type || 'image/jpeg' })
        );
      },
      type,
      IMAGE_ENCODE_QUALITY
    );
  });
}

export async function resizeImageIfNeeded(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImageElement(objectUrl);
    const { width, height } = image;

    if (width <= MAX_IMAGE_WIDTH && height <= MAX_IMAGE_HEIGHT) {
      return file;
    }

    const aspectRatio = width / height;
    let newWidth = width;
    let newHeight = height;

    if (width > MAX_IMAGE_WIDTH) {
      newWidth = MAX_IMAGE_WIDTH;
      newHeight = Math.round(newWidth / aspectRatio);
    }

    if (newHeight > MAX_IMAGE_HEIGHT) {
      newHeight = MAX_IMAGE_HEIGHT;
      newWidth = Math.round(newHeight * aspectRatio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(image, 0, 0, newWidth, newHeight);

    try {
      return await canvasToFile(canvas, file.name, file.type);
    } catch {
      return file;
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function getQueryParam(param: string) {
  const urlParams = new URLSearchParams(window.location.search);

  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === param.toLowerCase()) {
      return value;
    }
  }

  return null;
}

export function setQueryParam(param: string, value: string, reload = false) {
  const url = new URL(window.location.href);
  url.searchParams.set(param, value);
  window.history.pushState(null, '', url);

  if (reload) {
    window.location.reload();
  }
}

export function removeQueryParam(param: string, reload = false) {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  window.history.pushState(null, '', url);

  if (reload) {
    window.location.reload();
  }
}

export function normalizeAddress(address: string) {
  return address
    .replace(/, USA$/i, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();
}

export function hashStringToNumber(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    const char = str.codePointAt(i) ?? 0;
    hash = hash * 31 + char;
    hash = Math.abs(Math.trunc(hash));
  }
  return hash;
}

export function enableHorizontalScroll(event: WheelEvent<HTMLElement>): void {
  const el = event.currentTarget as HTMLElement;

  if (!el || el.scrollWidth <= el.clientWidth) {
    return;
  }

  if (!(event.target instanceof Node) || !el.contains(event.target)) {
    return;
  }

  const isTrackpad = Math.abs(event.deltaX) > 0 && Math.abs(event.deltaY) > 0;

  if (isTrackpad) {
    return;
  }

  el.style.overscrollBehaviorX = 'contain';
  el.style.overscrollBehaviorY = 'none';

  const combinedDelta = (event.deltaX || 0) + (event.deltaY || 0);
  const maxScrollLeft = el.scrollWidth - el.clientWidth;
  const nextLeft = Math.max(
    0,
    Math.min(maxScrollLeft, el.scrollLeft + combinedDelta)
  );

  event.preventDefault();
  event.stopPropagation();
  el.scrollLeft = nextLeft;
}

export function formatGroupedCounts(items: readonly string[]): string {
  if (items.length === 0) return '';
  const counts = new Map<string, number>();
  const order: string[] = [];
  items.forEach((item) => {
    if (!counts.has(item)) {
      order.push(item);
    }
    counts.set(item, (counts.get(item) ?? 0) + 1);
  });
  return order
    .map((item) => {
      const count = counts.get(item) ?? 0;
      return count > 1 ? `${count} x ${item}` : item;
    })
    .join(', ');
}

export function getClosestAirHandlerSize(btu: number): number {
  const btuInThousands = btu / 1000;
  return (
    airHandlerSizes.find((size) => size >= btuInThousands) ??
    airHandlerSizes.at(-1)!
  );
}
